export interface NavLeaf {
  kind: 'leaf';
  /** Stable id for keys (include path to avoid collisions). */
  id: string;
  label: string;
  to: string;
  /** WPF `NavigationDescriptor.Key` used to look up feature + permission. */
  wpfKey?: string;
  /**
   * WPF shell surface (Home, Home Advanced, Command Explorer): no Feature,
   * `PermissionLevel.None`. Kept by `PruneByFeature` even when a feature set is on.
   */
  shell?: boolean;
  /** Filename under `/icons/sidebar/` (copied from WPF `ColorIcons/Sidebar`). */
  iconFile?: string;
  /**
   * Custom active detection. Receives the current pathname and search
   * (`search` includes the leading `?`, or `''`).
   */
  match?: (pathname: string, search?: string) => boolean;
}

export interface NavGroup {
  kind: 'group';
  id: string;
  label: string;
  /** Filename under `/icons/sidebar/` (WPF sidebar color icons). */
  iconFile?: string;
  children: NavNode[];
}

/** WPF `Placeholder(...)` sidebar nodes — not yet productized. */
export interface NavPlaceholderLeaf {
  kind: 'placeholder';
  id: string;
  label: string;
  iconFile?: string;
}

export type NavNode = NavLeaf | NavGroup | NavPlaceholderLeaf;

/** Split a `leaf.to` (possibly with query) into pathname + search. */
export function splitNavTo(to: string): { pathname: string; search: string } {
  const hashIndex = to.indexOf('#');
  const withoutHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to;
  const queryIndex = withoutHash.indexOf('?');
  if (queryIndex < 0) {
    return { pathname: withoutHash, search: '' };
  }
  return {
    pathname: withoutHash.slice(0, queryIndex),
    search: withoutHash.slice(queryIndex),
  };
}

/** True when every key/value in `requiredSearch` is present in `currentSearch`. */
export function searchContainsRequired(currentSearch: string, requiredSearch: string): boolean {
  if (!requiredSearch) return true;
  const current = new URLSearchParams(
    currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch
  );
  const required = new URLSearchParams(
    requiredSearch.startsWith('?') ? requiredSearch.slice(1) : requiredSearch
  );
  for (const [key, value] of required.entries()) {
    if (current.get(key) !== value) return false;
  }
  return true;
}

export function pathMatchesTo(pathname: string, to: string, search = ''): boolean {
  const target = splitNavTo(to);
  if (target.pathname === '/') {
    return pathname === '/' && searchContainsRequired(search, target.search);
  }
  const pathOk =
    pathname === target.pathname || pathname.startsWith(`${target.pathname}/`);
  if (!pathOk) return false;
  return searchContainsRequired(search, target.search);
}

export function leafMatchesPath(pathname: string, leaf: NavLeaf, search = ''): boolean {
  if (leaf.match) return leaf.match(pathname, search);
  return pathMatchesTo(pathname, leaf.to, search);
}

/**
 * Group id chain that must be expanded for this location (excluding the leaf).
 * Prefers leaves whose `to` includes a matching search string over pathname-only matches,
 * so tile deep-links expand the workspace that owns the tile leaf.
 */
export function findAncestorGroupIds(
  pathname: string,
  nodes: NavNode[],
  chain: string[] = [],
  search = ''
): string[] | null {
  let bestChain: string[] | null = null;
  let bestSpecificity = 0;

  const visit = (navNodes: NavNode[], ancestorChain: string[]) => {
    for (const node of navNodes) {
      if (node.kind === 'leaf') {
        if (!leafMatchesPath(pathname, node, search)) continue;
        const target = splitNavTo(node.to);
        const specificity = target.search ? 2 : 1;
        if (specificity > bestSpecificity) {
          bestSpecificity = specificity;
          bestChain = ancestorChain;
        }
      } else if (node.kind === 'group') {
        visit(node.children, [...ancestorChain, node.id]);
      }
    }
  };

  visit(nodes, chain);
  return bestChain;
}
